import { Menu, Stack, Text } from '@mantine/core';

interface MoreRestrictionsMenuProps {
  /** Dietary restrictions the card had no room for. */
  restrictions: string[];
  /** Text size of the trigger, to match the surrounding badge row. */
  size?: string;
  /** Extra styles for the trigger (cards pass their remix-mode cursor). */
  style?: React.CSSProperties;
  /**
   * Portal the dropdown to the body (default true). Pass false inside mobile
   * bottom sheets, where body portals get mispositioned.
   */
  withinPortal?: boolean;
}

/**
 * "+N more" affordance for the dietary restrictions a card could not fit.
 * Clicking opens the full list, mirroring the recipe details view.
 *
 * The trigger is a real button, not styled text: it keeps the list reachable
 * by keyboard, and the meal plan carousel treats buttons as interactive so a
 * click here is never swallowed by its swipe handling.
 *
 * Clicks are stopped at the trigger so opening the list never fires the
 * surrounding card's own click handler (which usually opens the recipe).
 */
export const MoreRestrictionsMenu = ({
  restrictions,
  size = 'xs',
  style,
  withinPortal = true,
}: MoreRestrictionsMenuProps) => {
  if (restrictions.length === 0) {
    return null;
  }

  return (
    <Menu position="bottom-start" withinPortal={withinPortal}>
      <Menu.Target>
        <Text
          component="button"
          type="button"
          size={size}
          c="dimmed"
          data-no-carousel-drag="true"
          aria-label={`Show ${restrictions.length} more dietary restriction${
            restrictions.length === 1 ? '' : 's'
          }`}
          style={{
            // Strip the button chrome so it still reads as the dimmed caption
            // it replaced; fontFamily has to be explicit since the UA sets it
            // on button elements.
            background: 'none',
            border: 0,
            padding: 0,
            fontFamily: 'inherit',
            cursor: 'pointer',
            ...style,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          +{restrictions.length} more
        </Text>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <Menu.Label>Additional dietary restrictions</Menu.Label>
        <Stack gap={4} p="xs">
          {restrictions.map((restriction) => (
            <Text key={restriction} size="sm" c="dimmed">
              {restriction}
            </Text>
          ))}
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
};

export default MoreRestrictionsMenu;
